import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from '../context/LanguageContext';
import { getShoppingList } from '../lib/api';
import {
  ArrowLeft, ShoppingCart, Check, Copy, Share2, Crown, Info
} from 'lucide-react';
import { toast } from 'sonner';

const DAY_OPTIONS = [1, 2, 3, 7];

export default function ShoppingList() {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [selectedDays, setSelectedDays] = useState(7);
  const [data, setData] = useState(null);
  const [checkedItems, setCheckedItems] = useState({});
  const [error, setError] = useState('');

  const loadList = useCallback(async (days) => {
    setLoading(true);
    setError('');
    try {
      const res = await getShoppingList(days);
      setData(res.data);
      setCheckedItems({});
    } catch (err) {
      if (err.response?.status === 404) {
        setError(t('shoppingList.noPlan'));
      } else {
        setError(t('shoppingList.error'));
      }
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadList(selectedDays);
  }, [selectedDays, loadList]);

  const toggleItem = (catIdx, itemIdx) => {
    const key = `${catIdx}-${itemIdx}`;
    setCheckedItems(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const checkedCount = Object.values(checkedItems).filter(Boolean).length;

  const formatListAsText = () => {
    if (!data) return '';
    let text = `🛒 ${t('shoppingList.title')}\n`;
    text += `${data.day_names?.join(', ')}\n\n`;
    data.categories.forEach(cat => {
      text += `${cat.category}\n`;
      cat.items.forEach(item => {
        const key = `${data.categories.indexOf(cat)}-${cat.items.indexOf(item)}`;
        text += `${checkedItems[key] ? '✅' : '⬜'} ${item}\n`;
      });
      text += '\n';
    });
    return text.trim();
  };

  const handleCopy = async () => {
    const text = formatListAsText();
    try {
      if (navigator.share) {
        await navigator.share({ text });
      } else {
        await navigator.clipboard.writeText(text);
        toast.success(t('shoppingList.copied'));
      }
    } catch {
      try {
        await navigator.clipboard.writeText(text);
        toast.success(t('shoppingList.copied'));
      } catch {
        toast.error('Error');
      }
    }
  };

  const getDayLabel = (num) => {
    if (num === 7) return t('shoppingList.fullWeek');
    if (num === 1) return `1 ${t('shoppingList.dayUnit')}`;
    return `${num} ${t('shoppingList.daysUnit')}`;
  };

  // Category emoji map
  const getCategoryEmoji = (cat) => {
    const c = cat.toLowerCase();
    if (c.includes('frutta') || c.includes('verdura') || c.includes('fruit') || c.includes('vegetable')) return '🥬';
    if (c.includes('carne') || c.includes('pesce') || c.includes('meat') || c.includes('fish')) return '🥩';
    if (c.includes('latticin') || c.includes('uov') || c.includes('dairy') || c.includes('egg')) return '🥛';
    if (c.includes('cereal') || c.includes('pasta') || c.includes('grain')) return '🌾';
    if (c.includes('legum') || c.includes('protein')) return '🫘';
    if (c.includes('condiment') || c.includes('olio') || c.includes('oil')) return '🫒';
    if (c.includes('secca') || c.includes('semi') || c.includes('nuts') || c.includes('seed')) return '🥜';
    return '🧺';
  };

  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#080D1A]' : 'bg-white'}`} data-testid="shopping-list-page">
      {/* Header */}
      <div className={`fixed-header px-4 pt-4 pb-3 border-b ${isDark ? 'bg-[#080D1A] border-white/10' : 'bg-white border-gray-100'}`}>
        <div className="max-w-md mx-auto flex items-center gap-3">
          <button onClick={() => navigate(-1)} className={`p-2 rounded-full ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`} data-testid="shopping-back-btn">
            <ArrowLeft className={`w-5 h-5 ${isDark ? 'text-white' : 'text-gray-900'}`} />
          </button>
          <div className="flex-1">
            <h1 className={`font-display text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {t('shoppingList.title')}
            </h1>
            <p className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
              <Crown className="w-3 h-3 inline text-amber-500 mr-1" />
              {t('shoppingList.subtitle')}
            </p>
          </div>
          {data && (
            <button
              onClick={handleCopy}
              className={`p-2 rounded-full ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}
              data-testid="shopping-share-btn"
            >
              <Share2 className={`w-5 h-5 ${isDark ? 'text-white/60' : 'text-gray-400'}`} />
            </button>
          )}
        </div>
      </div>

      <div className="content-below-header pb-6">
        {/* Day selector */}
        <div className="px-4 pt-4 mb-4">
          <p className={`text-xs font-medium uppercase mb-2 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
            {t('shoppingList.selectDays')}
          </p>
          <div className="flex gap-2">
            {DAY_OPTIONS.map(num => (
              <button
                key={num}
                onClick={() => setSelectedDays(num)}
                className={`flex-1 py-2.5 rounded-xl text-sm font-medium transition-all ${
                  selectedDays === num
                    ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/25'
                    : isDark ? 'bg-white/5 text-white/60 border border-white/10' : 'bg-gray-100 text-gray-600'
                }`}
                data-testid={`shopping-days-${num}`}
              >
                {getDayLabel(num)}
              </button>
            ))}
          </div>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-20">
            <div className="spinner"></div>
          </div>
        )}

        {error && (
          <div className="px-4">
            <div className={`p-4 rounded-xl text-center ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
              <ShoppingCart className={`w-10 h-10 mx-auto mb-2 ${isDark ? 'text-white/20' : 'text-gray-300'}`} />
              <p className={`text-sm ${isDark ? 'text-white/50' : 'text-gray-500'}`}>{error}</p>
              <button
                onClick={() => navigate('/app/piano-alimentare')}
                className="mt-3 px-4 py-2 rounded-xl bg-emerald-500 text-white text-sm font-medium"
              >
                {t('shoppingList.createPlan')}
              </button>
            </div>
          </div>
        )}

        {data && !loading && (
          <>
            {/* Progress bar */}
            <div className="px-4 mb-4">
              <div className="glass-card p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                    {data.day_names?.join(', ')}
                  </span>
                  <span className={`text-xs font-medium ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>
                    {checkedCount}/{data.total_items}
                  </span>
                </div>
                <div className={`h-1.5 rounded-full overflow-hidden ${isDark ? 'bg-white/10' : 'bg-gray-200'}`}>
                  <div
                    className="h-full rounded-full bg-emerald-500 transition-all duration-300"
                    style={{ width: `${data.total_items > 0 ? (checkedCount / data.total_items) * 100 : 0}%` }}
                  />
                </div>
              </div>
            </div>

            {/* Categories */}
            <div className="px-4 space-y-3">
              {data.categories.map((cat, catIdx) => (
                <div key={cat.category} className="glass-card overflow-hidden" data-testid={`shopping-cat-${catIdx}`}>
                  <div className="p-3 pb-0">
                    <h3 className={`text-sm font-semibold flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      <span>{getCategoryEmoji(cat.category)}</span>
                      {cat.category}
                      <span className={`text-xs font-normal ${isDark ? 'text-white/30' : 'text-gray-400'}`}>
                        ({cat.items.length})
                      </span>
                    </h3>
                  </div>
                  <div className="p-2">
                    {cat.items.map((item, itemIdx) => {
                      const key = `${catIdx}-${itemIdx}`;
                      const isChecked = checkedItems[key];
                      return (
                        <button
                          key={key}
                          onClick={() => toggleItem(catIdx, itemIdx)}
                          className={`w-full flex items-center gap-3 p-2.5 rounded-xl transition-all ${
                            isChecked
                              ? isDark ? 'bg-emerald-500/10' : 'bg-emerald-50'
                              : 'hover:bg-white/5'
                          }`}
                          data-testid={`shopping-item-${catIdx}-${itemIdx}`}
                        >
                          <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                            isChecked
                              ? 'bg-emerald-500 border-emerald-500'
                              : isDark ? 'border-white/20' : 'border-gray-300'
                          }`}>
                            {isChecked && <Check className="w-3 h-3 text-white" />}
                          </div>
                          <span className={`text-sm text-left transition-all ${
                            isChecked
                              ? isDark ? 'text-white/30 line-through' : 'text-gray-400 line-through'
                              : isDark ? 'text-white/80' : 'text-gray-700'
                          }`}>
                            {item}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {/* Pantry Notes */}
            {data.notes && data.notes.length > 0 && (
              <div className={`mx-4 mt-4 p-4 rounded-2xl ${isDark ? 'bg-amber-500/10 border border-amber-500/15' : 'bg-amber-50 border border-amber-200'}`} data-testid="shopping-notes">
                <div className="flex items-center gap-2 mb-2">
                  <Info className={`w-4 h-4 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} />
                  <span className={`text-xs font-semibold uppercase ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>
                    {t('shoppingList.pantryNote')}
                  </span>
                </div>
                <p className={`text-xs leading-relaxed ${isDark ? 'text-amber-300/70' : 'text-amber-700'}`}>
                  {t('shoppingList.pantryReminder')}: {data.notes.join(', ')}.
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
